import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useThemeQuery } from '../../reactQuery/get';
import CompletionButton from './CompletionButton';

interface MessageContentParserProps {
  content: string;
  className?: string;
}

function MessageContentParser({ content, className }: MessageContentParserProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { organization_id, project_id, chat_id } = useParams();
  const projectId = organization_id ? project_id : chat_id;
  const { data: themeData } = useThemeQuery(projectId);

  // Function to convert plain URLs to clickable links
  const linkifyUrls = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      // Regex to match URLs
      const urlRegex = /(?:https?:\/\/|www\.)[^\s<]+(?:\.[^\s<]+)+(?:\/[^\s<]*)?/gi;

      if (urlRegex.test(text)) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        urlRegex.lastIndex = 0; // Reset regex
        while ((match = urlRegex.exec(text)) !== null) {
          // Add text before the URL
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }

          // Create link element
          const link = document.createElement('a');
          let href = match[0];
          // Add protocol if missing
          if (!href.startsWith('http://') && !href.startsWith('https://')) {
            href = `https://${href}`;
          }
          link.href = href;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.textContent = match[0];
          link.className = 'text-blue-500 underline hover:text-blue-700';
          fragment.appendChild(link);

          lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }

        // Replace the text node with the fragment
        if (node.parentNode) {
          node.parentNode.replaceChild(fragment, node);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip certain elements
      const element = node as HTMLElement;
      if (element.tagName !== 'A' && element.tagName !== 'CODE' && element.tagName !== 'PRE') {
        // Process child nodes - iterate backwards to avoid issues with DOM mutations
        const childNodes = Array.from(node.childNodes);
        // Process nodes in reverse order to handle DOM mutations safely
        for (let i = childNodes.length - 1; i >= 0; i--) {
          linkifyUrls(childNodes[i]);
        }
      }
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // First, convert markdown links to HTML
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const contentWithHtmlLinks = content.replace(markdownLinkRegex, (match, text, url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline hover:text-blue-700">${text}</a>`;
    });

    // Set the HTML content with converted links
    containerRef.current.innerHTML = contentWithHtmlLinks;

    // Convert remaining plain URLs to links
    linkifyUrls(containerRef.current);

    // Check if this message contains a completion link using data attributes
    const completionLink = containerRef.current.querySelector('[data-completion-link="true"]');

    if (completionLink) {
      const href = completionLink.getAttribute('href');
      // Since both URLs are the same, we use href for both
      const buttonText = completionLink.textContent || 'Complete Interview';
      const autoRedirect = completionLink.getAttribute('data-auto-redirect') === 'true';

      /**
       * Extract localized strings from data attributes
       * These are embedded by the backend based on user's language preference
       */
      const redirectingIn = completionLink.getAttribute('data-redirecting-in') || 'Redirecting in';
      const seconds = completionLink.getAttribute('data-seconds') || 'seconds...';
      const cancel = completionLink.getAttribute('data-cancel') || 'Cancel';

      if (href) {
        // Extract the fallback text (text after the link but before the code element)
        let fallbackText = "Can't click the button? Copy this link:";
        let currentNode = completionLink.nextSibling;
        while (currentNode) {
          if (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim()) {
            fallbackText = currentNode.textContent.trim();
            break;
          }
          if (
            currentNode.nodeType === Node.ELEMENT_NODE &&
            (currentNode as HTMLElement).tagName === 'CODE'
          ) {
            break;
          }
          currentNode = currentNode.nextSibling;
        }

        // Find the parent of the completion link to replace the whole section
        const linkParent = completionLink.parentElement;

        // Create a container for the button component
        const buttonContainer = document.createElement('div');

        // Replace the link and its surrounding text with the button container
        if (linkParent) {
          // Remove the completion link section (link + br + fallback text + br + code)
          const nodesToRemove = [];
          let currentNode = completionLink.previousSibling;
          // Remove text before link (completion message)
          while (currentNode && currentNode.nodeType === Node.TEXT_NODE) {
            nodesToRemove.push(currentNode);
            currentNode = currentNode.previousSibling;
          }
          // Remove link and everything after it including the code element
          currentNode = completionLink;
          while (currentNode) {
            nodesToRemove.push(currentNode);
            currentNode = currentNode.nextSibling;
            // Stop after we've included the code element
            if (
              nodesToRemove[nodesToRemove.length - 1].nodeType === Node.ELEMENT_NODE &&
              (nodesToRemove[nodesToRemove.length - 1] as HTMLElement).tagName === 'CODE'
            ) {
              break;
            }
          }

          // Remove all collected nodes
          nodesToRemove.forEach((node) => node.remove());

          // Insert button container
          linkParent.appendChild(buttonContainer);
        }

        // Use React Portal to render the button component
        import('react-dom/client').then(({ createRoot }) => {
          const root = createRoot(buttonContainer);
          root.render(
            <CompletionButton
              url={href}
              text={buttonText}
              fallbackText={fallbackText}
              autoRedirect={autoRedirect}
              primaryColor={themeData?.primary}
              chatPrimary={themeData?.['chat-primary']}
              chatSecondary={themeData?.['chat-secondary']}
              translations={{
                redirectingIn,
                seconds,
                cancel,
                copied: 'Copied!',
                cantClickButton: fallbackText,
              }}
            />,
          );
        });
      }
    }
  }, [content, themeData]);

  return <div ref={containerRef} className={className} dir="auto" />;
}

export default MessageContentParser;
